import { PropsWithChildren, ReactElement, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { css, keyframes } from '../../Styles/themed-styled-components';

const slideIn = keyframes`
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
`;

const slideOut = keyframes`
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(100%);
  }
`;

const OverlayBackground = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1300;
  display: flex;
  justify-content: flex-end;
`;

const Panel = styled.div<{ isClosing: boolean }>`
  background: white;
  width: 100vw;
  height: 100vh;
  ${({ isClosing }) => css`
    animation: ${isClosing ? slideOut : slideIn} 0.3s ease-out forwards;
  `}
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.1);
`;

export function AssistedApplicationOverlay({
  children,
  isOpen,
}: PropsWithChildren<{ isOpen: boolean }>): ReactElement | null {
  const [shouldRender, setShouldRender] = useState(isOpen);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShouldRender(true);
      setIsClosing(false);
      return;
    }
    if (shouldRender) {
      setIsClosing(true);
      const timer = setTimeout(() => {
        setShouldRender(false);
        setIsClosing(false);
      }, 310);
      return () => clearTimeout(timer);
    }
    return;
  }, [isOpen, shouldRender]);

  const overlayRoot = document.getElementById('overlay-root');
  if (!overlayRoot) {
    return null;
  }

  if (!shouldRender) return null;

  return createPortal(
    <OverlayBackground>
      <Panel isClosing={isClosing}>{children}</Panel>
    </OverlayBackground>,
    overlayRoot,
  );
}
